import React, { useMemo } from 'react';

/**
 * V6.5 — Annotations de patchs sous un graphique temporel
 *
 * Affiche une bande SVG colorée en bas du chart, segmentée par patch.
 * Chaque segment est positionné selon la fenêtre de games qui correspond au patch.
 *
 * Utilisation :
 *   <ResponsiveContainer>...</ResponsiveContainer>
 *   <PatchAnnotations games={filteredGames} startGameNumber={1} />
 *
 * Props :
 *   games            - Array des games dans l'ordre chronologique (avec g.patch)
 *   startGameNumber  - Numéro de la 1ère game (par défaut 1, peut différer si rolling/sliced)
 *   xAxisKey         - Pour aligner avec un axe X custom (par défaut, on utilise l'index)
 *
 * Principe :
 *   On scanne les games dans l'ordre, on regroupe les segments contigus de même patch.
 *   On affiche chaque segment avec sa couleur et son label.
 */

// Hash patch → couleur stable (chaque patch a sa couleur unique)
const PATCH_PALETTE = [
  { fill: '#dc2626', text: '#fca5a5', name: 'red' },      // 26.08 (rouge — comme dans ton mockup)
  { fill: '#0891b2', text: '#67e8f9', name: 'cyan' },     // 26.09 (cyan)
  { fill: '#16a34a', text: '#86efac', name: 'green' },    // 26.10 (vert)
  { fill: '#ca8a04', text: '#fde047', name: 'yellow' },
  { fill: '#9333ea', text: '#d8b4fe', name: 'purple' },
  { fill: '#db2777', text: '#f9a8d4', name: 'pink' },
  { fill: '#ea580c', text: '#fdba74', name: 'orange' },
  { fill: '#0d9488', text: '#5eead4', name: 'teal' }
];

function getPatchColor(patch, allPatches) {
  // Plus le patch est récent, plus l'index dans allPatches est bas (descending order)
  // On veut que les patchs récents aient des couleurs distinctes
  const idx = allPatches.indexOf(patch);
  return PATCH_PALETTE[idx % PATCH_PALETTE.length];
}

export default function PatchAnnotations({ games, startGameNumber = 1, height = 28 }) {
  // Construire les segments de patchs
  const segments = useMemo(() => {
    if (!games || games.length === 0) return [];

    // Lister les patchs uniques pour cohérence des couleurs
    const allUniquePatches = Array.from(new Set(
      games.map(g => g.patch).filter(Boolean)
    )).sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

    const segs = [];
    let currentPatch = null;
    let segStart = 0;

    games.forEach((g, i) => {
      const patch = g.patch || 'unknown';
      if (patch !== currentPatch) {
        // Fermer le segment précédent
        if (currentPatch !== null) {
          segs.push({
            patch: currentPatch,
            start: segStart,
            end: i - 1,
            color: getPatchColor(currentPatch, allUniquePatches)
          });
        }
        currentPatch = patch;
        segStart = i;
      }
    });
    // Fermer le dernier
    if (currentPatch !== null) {
      segs.push({
        patch: currentPatch,
        start: segStart,
        end: games.length - 1,
        color: getPatchColor(currentPatch, allUniquePatches)
      });
    }

    return segs;
  }, [games]);

  if (segments.length === 0 || games.length === 0) return null;

  // Si tous les segments sont 'unknown', on n'affiche rien
  if (segments.every(s => s.patch === 'unknown')) return null;

  const totalGames = games.length;

  return (
    <div className="mt-1 px-[60px] md:px-[80px]" style={{ height }}>
      <div className="relative w-full h-full">
        {segments.map((seg, i) => {
          const widthPct = ((seg.end - seg.start + 1) / totalGames) * 100;
          const leftPct = (seg.start / totalGames) * 100;
          // Petit segment : on cache le label
          const showLabel = widthPct > 6;
          const isUnknown = seg.patch === 'unknown';
          return (
            <div
              key={i}
              className="absolute top-0 h-full flex items-center justify-center transition"
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                backgroundColor: isUnknown ? 'rgba(82, 82, 91, 0.3)' : seg.color.fill,
                borderRight: i < segments.length - 1 ? '1px solid rgba(0, 0, 0, 0.4)' : 'none'
              }}
              title={`Patch ${seg.patch} — Games ${seg.start + startGameNumber} à ${seg.end + startGameNumber}`}
            >
              {showLabel && (
                <span
                  className="text-[10px] font-mono font-bold tracking-wider truncate px-1"
                  style={{ color: isUnknown ? '#a1a1aa' : '#000' }}
                >
                  {isUnknown ? '?' : seg.patch}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
